import { useState, useEffect } from 'react'
import { SiteOption } from '../../../types/SiteOption'
import { BrandOption } from '../../../types/BrandOption'
import { FixedFee, FixedFeeRow } from '../../../types/FixedFee'
import { buildFixedFeeQuery, fixedFeeScopeLabel, isFixedFeeLocked, isFixedFeeMaterializedForMe } from '../utils/fixedFeeUtils'
import BackOfficeAPIClient from '../../../clients/BackofficeAPIClient'

const backofficeApiClient = new BackOfficeAPIClient()
let nextRowId = 0

interface UseFixedFeesReturn {
    rows: FixedFeeRow[]
    selectedSite: SiteOption | null
    setSelectedSite: (site: SiteOption | null) => void
    selectedBrand: BrandOption | null
    setSelectedBrand: (brand: BrandOption | null) => void
    hasChanges: boolean
    submitting: boolean
    loading: boolean
    isSiteMode: boolean
    isBrandMode: boolean
    isLocked: (row: FixedFeeRow) => boolean
    scopeLabel: (row: FixedFeeRow) => string
    updateField: (rowId: number, field: string, value: any) => void
    addRow: () => void
    deleteRow: (rowId: number) => void
    save: () => Promise<void>
}

/**
 * Modèle "matérialisation + cascade" (cf. InspectionFixedFeeTemplatesController.js) : une seule
 * liste de lignes (`rows`), qu'on soit sur le catalogue global, un site, une marque, ou une
 * combinaison site+marque. Une ligne est soit une "racine" (source_id null — éditable/supprimable
 * à son propre scope), soit un "miroir" verrouillé (source_id posé — actif/inactif seulement).
 *
 * Tant qu'un scope exact (site, marque, ou site+marque) n'a jamais été enregistré, l'API renvoie
 * des lignes par défaut "empruntées" (le catalogue global, ou — pour une combinaison — la fusion
 * site/marque dédoublonnée) : ce sont des racines ailleurs, pas encore matérialisées ICI. On les
 * traite comme verrouillées tant qu'elles ne sont pas "à moi" (cf. isMaterializedForMe), et
 * l'enregistrement les matérialise pour de bon (avec la bonne origine).
 */
export function useFixedFees(skipFetch = false): UseFixedFeesReturn {
    const [ rows, setRows ] = useState<FixedFeeRow[]>([])
    const [ selectedSite, setSelectedSite ] = useState<SiteOption | null>(null)
    const [ selectedBrand, setSelectedBrand ] = useState<BrandOption | null>(null)
    const [ hasChanges, setHasChanges ] = useState(false)
    const [ submitting, setSubmitting ] = useState(false)
    const [ loading, setLoading ] = useState(true)

    const selectedSiteId = selectedSite?.value ?? null
    const selectedBrandValue = selectedBrand?.value ?? null
    const isSiteMode = selectedSite !== null
    const isBrandMode = selectedBrand !== null

    useEffect(() => {
        // skipFetch volontairement absent des dépendances : en édition, tant que la cible finale
        // (site+marque) n'est pas connue, le préremplissage pose selectedSite puis selectedBrand
        // en deux temps (cf. FixedFees.tsx) — sans cette porte on fetcherait une fois pour chaque
        // étape intermédiaire au lieu d'une seule fois avec les deux valeurs définitives.
        if (skipFetch) {
            return
        }
        loadConfig()
        setHasChanges(false)
    }, [ selectedSiteId, selectedBrand ])

    const loadConfig = async () => {
        setLoading(true)
        try {
            const data = await backofficeApiClient.get<FixedFee[]>(`/inspection-fixed-fee-templates${buildFixedFeeQuery(selectedSiteId, selectedBrandValue)}`)
            setRows(data.map(row => ({ ...row, amount: row.amount / 100, rowId: nextRowId++ })))
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const isMaterializedForMe = (row: FixedFee): boolean => isFixedFeeMaterializedForMe(row, selectedSiteId, selectedBrandValue)
    const isLocked = (row: FixedFeeRow): boolean => isFixedFeeLocked(row, selectedSiteId, selectedBrandValue)
    const scopeLabel = (row: FixedFeeRow): string => fixedFeeScopeLabel(row, selectedSiteId, selectedBrandValue)

    const markDirty = () => setHasChanges(true)

    const updateField = (rowId: number, field: string, value: any) => {
        setRows(prev => prev.map(row => row.rowId === rowId ? { ...row, [field]: value } : row))
        markDirty()
    }

    const addRow = () => {
        setRows(prev => [ ...prev, {
            rowId: nextRowId++,
            dealership_id: selectedSiteId,
            brand: selectedBrand?.value ?? null,
            fee_type: '',
            amount: 0,
            is_active: true,
            source_id: null,
            origin: null,
            isNew: true,
        } ])
        markDirty()
    }

    const deleteRow = (rowId: number) => {
        setRows(prev => prev.filter(row => row.rowId !== rowId))
        markDirty()
    }

    const save = async () => {
        setSubmitting(true)
        try {
            const payload = rows.map(row => {
                const mine = isMaterializedForMe(row)
                return {
                    id: mine ? row.id : undefined,
                    // Racine empruntée pas encore matérialisée ici : source_id doit pointer sur
                    // elle-même (row.id), jamais sur le scope d'où elle a été empruntée.
                    source_id: mine ? row.source_id : (row.source_id ?? row.id ?? null),
                    fee_type: row.fee_type,
                    amount: Math.round(row.amount * 100),
                    is_active: row.is_active,
                }
            })

            await backofficeApiClient.put(`/inspection-fixed-fee-templates${buildFixedFeeQuery(selectedSiteId, selectedBrandValue)}`, payload)

            await loadConfig()
            setHasChanges(false)
        } catch (error) {
            console.log(error)
        } finally {
            setSubmitting(false)
        }
    }

    return {
        rows,
        selectedSite,
        setSelectedSite,
        selectedBrand,
        setSelectedBrand,
        hasChanges,
        submitting,
        loading,
        isSiteMode,
        isBrandMode,
        isLocked,
        scopeLabel,
        updateField,
        addRow,
        deleteRow,
        save,
    }
}
